import React, { useState, useEffect, Fragment } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics';
import type { AIEventsInstrumentationConfig } from '@atlassian/ai-analytics';
import { useAIGeneratedCoverImage } from '@atlassian/ai-image-generation';

import { EditorTitle } from '@confluence/editor-title';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { TitleAlignmentType } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { useTitleContentPropertiesForEditor } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForEditor.ts';
import { CoverPictureStateContainer } from '@confluence/page-cover-picture/entry-points/CoverPictureStateContainer.ts';
import { LivePagesTitle } from '@confluence/editor-title/entry-points/LivePagesTitle.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useContentClassification } from '@confluence/data-classification/entry-points/queries.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { useGeneratedTitle } from '@confluence/title-area-utils/entry-points/useGeneratedTitle.ts';
import { useImageGenerationCallbacks } from '@confluence/title-area-utils/entry-points/useImageGenerationCallbacks.ts';
import { expVal, isExperimentEnabled } from '@confluence/feature-experiments';
import {
	ONBOARDING_EXPERIENCES,
	NTH_USER_EMOJI_TITLE_DISCOVERED,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { isLocaleEnglish } from '@confluence/onboarding-helpers/entry-points/isLocaleEnglish.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { EditorTitleWithTitleAreaComponents } from './EditorTitleWithTitleAreaComponents.tsx';
import type {
	AutoPageEmojiCohort,
	TitleAreaContentComponentsProps,
} from './titleareaContentComponentsTypes.ts';
import { AlignmentButtonContainer } from './AlignmentButtonContainer.tsx';

const i18n = defineMessages({
	autoPageEmojiPlaceholderTitle: {
		id: 'title-area-content-components.title-section.auto-page-emoji-placeholder-title',
		defaultMessage: 'Start with a title and emoji',
		description:
			'placeholder title for users enrolled in the auto page emoji experiment prompting them to add a page title and emoji.',
	},
});

const topLineBoxStylesMarginTopSpaceNegative300 = xcss({
	marginTop: 'space.negative.300',
});

const breadcrumbAndTitleGapStyles = xcss({
	marginBottom: 'space.300',
});

const aiAnalyticsConfig: AIEventsInstrumentationConfig = {
	product: 'confluence',
	subproduct: 'confluence',
	source: 'confluencePage',
	aiFeatureName: 'aiTitleGenOnPublish',
	proactiveGeneratedAI: 1,
	userGeneratedAI: 0,
};

type TitleAreaComponentWithState = TitleAreaContentComponentsProps & {
	alignmentButtonContainer: AlignmentButtonContainer;
	coverPictureStateContainer: CoverPictureStateContainer;
	flags: FlagsStateContainer;
	contentComponentsDisabled?: boolean;
	isStatusDisconnected?: boolean;
};

const TitleAreaComponentWithState = ({
	title,
	breadcrumbsComponent,
	contentId,
	cloudId,
	isDisabled,
	linkCreation,
	onChange,
	focusEditor,
	onFocusChanged,
	debounceOnChange,
	contentType,
	userOffline,
	isInPreview,
	isUnpublishedDraft,
	isPublishingContent,
	templateSuggestionsRef,
	templateButtonRef,
	isTemplateEditor = false,
	contentTopperComponent: headerActionsComponent,
	viewMode,
	contentClassificationMetadata,
	version,
	isEditorEmpty,
	alignmentButtonContainer,
	coverPictureStateContainer,
	flags,
	contentComponentsDisabled,
	isStatusDisconnected,
	editorApi,
	editorSessionId,
	isSeamlessTransitionInProgress = false,
	isTitleGenerating,
	onTitleGeneratingChange,
}: TitleAreaComponentWithState) => {
	const spaceId = useSpaceId();
	const { isAdminHubAIEnabled } = useSessionData();
	const { formatMessage, locale } = useIntl();

	const updateAlignmentState = alignmentButtonContainer?.toggleAlignmentButton;
	const updateCoverPictureWidthState = coverPictureStateContainer?.toggleCoverPictureWidthButton;
	const updateIsLoadingComplete = alignmentButtonContainer?.setIsLoadingComplete;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const hideWhileLoading = !(window.__SSR_RENDERED__ || process.env.REACT_SSR);
	const { isTitleAlignmentLoading, isTitleCenteredInitial, coverImageWidthCached } =
		useTitleContentPropertiesForEditor({
			contentId,
			updateAlignmentState,
			updateCoverPictureWidthState,
			loadTitleContentProperties: true,
			contentType,
			flags,
			updateIsLoadingComplete,
		});

	const [localUserOffline, setLocalUserOffline] = useState<boolean>(false);
	const isLivePage = useIsCurrentPageLive();
	const [spaceKey = ''] = usePageSpaceKey();
	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, action] = useGeneratedTitle();

	const [titleAfterBlur, setTitleAfterBlur] = useState<string | null>(null);
	const onTitleBlur = (currentTitle: string | undefined) => {
		setTitleAfterBlur(currentTitle ?? title);
	};

	const isEnglishLocale = isLocaleEnglish(locale);
	const isEligibleForAutoPageEmojiAudienceCheck =
		isAdminHubAIEnabled &&
		isEnglishLocale &&
		(contentType === 'page' || contentType === 'blogpost');

	const { data: onboardingStateData } = useGetOnboardingState(
		[
			ONBOARDING_EXPERIENCES.NTH_USER_ONBOARDING_COMPLETED_TIMESTAMP,
			NTH_USER_EMOJI_TITLE_DISCOVERED.EMOJI_TITLE_DISCOVERED,
		],
		!isEligibleForAutoPageEmojiAudienceCheck,
	);
	const { nthUserOnboardingCompletedTimestamp, emojiTitleDiscovered } =
		deserializeState(onboardingStateData);
	const completedTimestamp = parseInt(nthUserOnboardingCompletedTimestamp, 10) ?? 0;

	const isEligibleForAutoPageEmojiExp =
		isEligibleForAutoPageEmojiAudienceCheck && completedTimestamp >= Date.parse('2025-12-04');

	const cohort = isEligibleForAutoPageEmojiExp
		? expVal('auto_page_emoji_for_new_users_exp', 'cohort', 'not-enrolled')
		: undefined;

	const autoPageEmojiCohort: AutoPageEmojiCohort | undefined =
		cohort === 'control' || cohort === 'random' || cohort === 'ai' || cohort === 'not-enrolled'
			? cohort
			: undefined;

	const customPlaceholder =
		autoPageEmojiCohort === 'random' || autoPageEmojiCohort === 'ai'
			? formatMessage(i18n.autoPageEmojiPlaceholderTitle)
			: undefined;

	const contentClassificationFromHook = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext: isLivePage ? 'current-and-draft' : 'draft-only',
	});

	const contentClassification = !contentClassificationMetadata
		? contentClassificationFromHook
		: contentClassificationMetadata;

	const { shouldDisplayClassification } = contentClassification;

	// Experience tracking callbacks for AI image generation
	const imageGenerationCallbacks = useImageGenerationCallbacks();

	const { generateImage, aiHeaderImageStatus, aiHeaderImageFileId, setAIHeaderImageStatus } =
		useAIGeneratedCoverImage({
			contentId,
			eventCallbacks: imageGenerationCallbacks,
		});

	// Connection banner height has a delay to appear, adjust the draft banner height to reflect this delay
	useEffect(() => {
		const DELAY_TIMEOUT = 3000;
		const delayTimeout: ReturnType<typeof setTimeout> = setTimeout(
			() => setLocalUserOffline(userOffline),
			DELAY_TIMEOUT,
		);

		return () => {
			clearTimeout(delayTimeout);
		};
	}, [userOffline]);

	const renderTitleComponent = () => {
		if (isTitleAlignmentLoading && hideWhileLoading) {
			return null;
		}
		return (
			<Subscribe to={[FlagsStateContainer, AlignmentButtonContainer]}>
				{(flags: FlagsStateContainer, alignmentButtonContainer: AlignmentButtonContainer) => {
					const isTitleCenterAligned = !alignmentButtonContainer.state.isLoadingComplete
						? isTitleCenteredInitial
						: alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.CENTER;

					return (
						<AIEventsInstrumentationProvider config={aiAnalyticsConfig}>
							{isLivePage ? (
								<LivePagesTitle
									title={title ?? ''}
									contentId={contentId}
									cloudId={cloudId}
									userOffline={userOffline}
									isDisabled={isDisabled}
									isEditorEmpty={isEditorEmpty}
									focusEditor={focusEditor}
									onFocusChanged={onFocusChanged}
									onTitleBlur={onTitleBlur}
									viewMode={viewMode}
									isTitleCenterAligned={isTitleCenterAligned}
									version={version}
									editorApi={editorApi}
									editorSessionId={editorSessionId}
									isSeamlessTransitionInProgress={isSeamlessTransitionInProgress}
									customPlaceholder={customPlaceholder}
									flags={flags}
								/>
							) : (
								<EditorTitle
									title={title}
									contentId={contentId}
									cloudId={cloudId}
									isDisabled={isDisabled}
									onChange={onChange}
									focusEditor={focusEditor}
									onFocusChanged={onFocusChanged}
									setGeneratedTitle={action.setGeneratedTitle}
									setTrackAIResultViewStored={action.setTrackAIResultViewStored}
									onTitleBlur={onTitleBlur}
									debounceOnChange={debounceOnChange}
									contentType={contentType}
									userOffline={userOffline}
									isInPreview={isInPreview}
									isTemplateEditor={isTemplateEditor}
									templateSuggestionsRef={templateSuggestionsRef}
									templateButtonRef={templateButtonRef}
									flagArray={flags?.state?.flags ?? []}
									isTitleCenterAligned={isTitleCenterAligned}
									isUnpublishedDraft={isUnpublishedDraft}
									contentClassificationMetadata={contentClassification}
									hasClassification={shouldDisplayClassification}
									isEditMode
									isEditorEmpty={isEditorEmpty}
									spaceKey={spaceKey}
									spaceId={spaceId}
									editorApi={editorApi}
									editorSessionId={editorSessionId}
									isSeamlessTransitionInProgress={isSeamlessTransitionInProgress}
									isTitleGeneratingFromPaste={isTitleGenerating}
									onTitleGeneratingChange={onTitleGeneratingChange}
									customPlaceholder={customPlaceholder}
								/>
							)}
						</AIEventsInstrumentationProvider>
					);
				}}
			</Subscribe>
		);
	};

	return (
		<Fragment>
			{isTemplateEditor && (
				<Box
					xcss={[
						localUserOffline && topLineBoxStylesMarginTopSpaceNegative300,
						breadcrumbAndTitleGapStyles,
					]}
					testId="breadcrumbs-wrapper"
				>
					{breadcrumbsComponent}
				</Box>
			)}
			<EditorTitleWithTitleAreaComponents
				contentId={contentId}
				isPublishingContent={isPublishingContent}
				contentType={contentType}
				userOffline={userOffline}
				isTemplateEditor={isTemplateEditor}
				isTitleDisabled={isDisabled}
				isUnpublishedDraft={isUnpublishedDraft}
				isTitleCenterAligned={
					!alignmentButtonContainer.state.isLoadingComplete
						? isTitleCenteredInitial
						: alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.CENTER
				}
				linkCreation={linkCreation}
				viewMode={viewMode}
				contentTopperComponent={!isTemplateEditor && headerActionsComponent}
				contentClassificationMetadata={contentClassification}
				hasClassification={shouldDisplayClassification}
				spaceKey={spaceKey}
				isEditorEmpty={isEditorEmpty}
				isTitleAlignmentLoading={isTitleAlignmentLoading && hideWhileLoading}
				contentComponentsDisabled={contentComponentsDisabled}
				isStatusDisconnected={isStatusDisconnected}
				coverPictureState={coverPictureStateContainer}
				titleAfterBlur={titleAfterBlur}
				isLivePage={isLivePage}
				coverImageWidthCached={coverImageWidthCached}
				pageTitleContent={titleAfterBlur || undefined}
				aiHeaderImageStatus={
					isExperimentEnabled('confluence_aifc_header_image_generation_experiment')
						? aiHeaderImageStatus
						: undefined
				}
				aiHeaderImageFileId={
					isExperimentEnabled('confluence_aifc_header_image_generation_experiment')
						? aiHeaderImageFileId
						: undefined
				}
				generateAIHeaderImage={
					isExperimentEnabled('confluence_aifc_header_image_generation_experiment')
						? generateImage
						: undefined
				}
				setAIHeaderImageStatus={
					isExperimentEnabled('confluence_aifc_header_image_generation_experiment')
						? setAIHeaderImageStatus
						: undefined
				}
				autoPageEmojiCohort={autoPageEmojiCohort}
				emojiTitleDiscovered={emojiTitleDiscovered}
			>
				{renderTitleComponent()}
			</EditorTitleWithTitleAreaComponents>
		</Fragment>
	);
};
TitleAreaComponentWithState.displayName = 'TitleAreaComponentWithState';

export const TitleAreaContentComponents = (props: TitleAreaContentComponentsProps) => {
	return (
		<Subscribe to={[AlignmentButtonContainer, CoverPictureStateContainer, FlagsStateContainer]}>
			{(
				alignmentButtonContainer: AlignmentButtonContainer,
				coverPictureStateContainer: CoverPictureStateContainer,
				flags: FlagsStateContainer,
			) => (
				<TitleAreaComponentWithState
					{...props}
					alignmentButtonContainer={alignmentButtonContainer}
					coverPictureStateContainer={coverPictureStateContainer}
					flags={flags}
				/>
			)}
		</Subscribe>
	);
};
TitleAreaContentComponents.displayName = 'TitleAreaContentComponents';
